/**
 * @fileoverview Everything you can do to a photo that already exists.
 *
 * The screen owns the two fields filed against the picture — its date and the
 * words about it — and saving those is the one errand written out here,
 * because it is the one the fields are for. No stage: a photo carries none
 * (2026-10-03); a plant's stage is its stage history, changed on its card. The other four (download,
 * replace, move, delete) live in usePhotoActions: they share a subject with
 * this screen and nothing else.
 *
 * The picture can be turned and cropped right here (2026-10-06): Rotate
 * left, Rotate right and Crop sit under it, the preview turns at once, and
 * Save sends the edit first, before the date and words. While an edit is
 * pending, the buttons on the picture (Replace, Download) step aside, as
 * they act on the picture as stored. Once a photo has been edited,
 * a quiet "Reset to original" puts the upload back, at once.
 *
 * Where: a photo on a plant's or a bed's card, tapped (through
 * PhotoUploadDialog with editMode).
 * API: PUT /photos/:plantId/:photoId, or PUT /locations/:id/photos/:photoId
 * (updatePhotoFor: date and note id); PUT …/:photoId/image and
 * …/image/reset (editPhotoImageFor, resetPhotoImageFor); the words through
 * usePhotoNote (/notes, or /locations/:id/journal); download, replace, move
 * and delete through usePhotoActions.
 * Settings: VITE_GOOGLE_CLIENT_ID (the Google Photos row, for Replace).
 * Words (en/es): plants.photo.* (editTitle, replaceTitle, save, deleteAction,
 * moved, movedTo, moveFailed, saveFailed, deleteFailed, keepPicture,
 * replacing, replace, download, thisAlt, deleteTitle), plants.photo.edit.*
 * (cropHint, failed, resetFailed), sitewide.cancel, sitewide.saving (Button loading).
 * Watch out: the edit is sent before the date and words, so a failed edit
 * leaves those unsaved too, and the dialog open.
 * A failure on our side (D31, lib/serverFailure) is said with the standard
 * sentence through errorMessage, drawn with the support address as a link.
 *
 * @module EditPhotoDialog
 */

import React, { useContext } from 'react';
import { Download, RefreshCw } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import Spinner from '@/shared/components/ui/Spinner';
import { ActionRow } from '@/shared/components/ui/ActionRow';
import { getPhotoUrl } from '@/shared/utils/imageUtils';
import { DateTime, calendarDay } from '@/shared/utils/dateUtils';
import { PlantPhoto } from '@/shared/types/photo';
import { PlantsContext } from '@/shared/context/PlantsContextDefinition';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useToast } from '@/shared/components/ui/use-toast';
import { debugLog } from '@/config/config';
import { errorMessage } from '@/shared/lib/errorMessage';
import { FailureText } from '@/shared/components/ServerFailure';
import { editPhotoImageFor, resetPhotoImageFor, updatePhotoFor } from '@/shared/api/photos';
import { isEditedPhoto } from '@/shared/lib/imageEdit';
import { plantSubject, type Subject } from '@/shared/api/subject';
import { preparePhoto } from '@/shared/lib/preparePhoto';
import { PhotoNoteField } from '../PhotoNoteField';
import { PhotoDialogShell, PhotoPicture } from './PhotoDialogShell';
import { PhotoButton } from '@/shared/components/ui/PhotoButton';
import { PhotoSourcePicker } from './PhotoSourcePicker';
import { DateTakenField } from './DateTakenField';
import { MoveToPlantField } from './MoveToPlantField';
import { DeletePhotoConfirm } from './DeletePhotoConfirm';
import { useCamera } from './useCamera';
import { useGooglePhotos } from './useGooglePhotos';
import { usePhotoNote } from './usePhotoNote';
import { usePhotoActions } from './usePhotoActions';
import { useImageEdit } from './useImageEdit';
import { ImageEditStage } from './ImageEditStage';
import { ImageEditTools } from './ImageEditTools';
import { plantName } from '@/shared/lib/plantName';

/** The photo being edited, whose it is, and how the card hears what happened. */
interface EditPhotoDialogProps {
  /** Whether the dialog is showing; closing it forgets everything this visit did. */
  isOpen: boolean;
  /** Closes it. */
  onClose: () => void;
  /** Used only to build a fallback image URL for photos with no signed one. */
  userId: number;
  /** The plant the photo is on (or the bed's id, with a location subject). */
  plantObjId: number;
  /** A location's photo instead of a plant's: no stage, no moving. */
  subject?: Subject;
  /** The photo being edited; nothing is drawn without one. */
  photo: PlantPhoto | undefined;
  /** Told of the photo as it now stands, after a save, an edit, a reset or a replacement. */
  onPhotoUpdated: (photo: PlantPhoto) => void;
  /** Told when the photo has left the card: deleted, or moved to another plant. */
  onPhotoDeleted?: (photoId: number) => void;
}

/**
 * The screen for a photo that exists: its date and words, and download, replace, move and delete.
 *
 * @param props - EditPhotoDialogProps.
 */
export const EditPhotoDialog: React.FC<EditPhotoDialogProps> = ({
  isOpen, onClose, userId, plantObjId, subject: subjectProp, photo, onPhotoUpdated, onPhotoDeleted,
}) => {
  /** The plant or bed the photo is on; one object while the props stay the same. */
  const subject = React.useMemo(() => subjectProp ?? plantSubject(plantObjId), [subjectProp, plantObjId]);
  const isPlant = subject.kind === 'plant';
  const { reactTranslate: t } = useTranslation();
  const { toast } = useToast();
  const camera = useCamera();
  /** Google Photos for Replace; kept with the photo, so a pick that comes after this closes still replaces it (B40). */
  const google = useGooglePhotos(isOpen, photo
    ? { kind: 'replace', id: photo.photo_id, ...(subject.kind === 'plant' ? { plantId: subject.id } : { locationId: subject.id }) }
    : undefined);
  const note = usePhotoNote(subject, photo, isOpen);

  const [dateTaken, setDateTaken] = React.useState<Date>(() => new Date());
  const [isSaving, setIsSaving] = React.useState(false);
  const [isImageLoading, setIsImageLoading] = React.useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
  /** True while the gardener is choosing a different picture for this photo. */
  const [isChoosingPicture, setIsChoosingPicture] = React.useState(false);
  /** True while a replacement picture is being got ready (converted, shrunk, dated), before it is sent. */
  const [isPreparing, setIsPreparing] = React.useState(false);
  /** Why the replacement chosen could not be used (an iPhone photo that would not convert), or null. */
  const [pictureError, setPictureError] = React.useState<string | null>(null);
  const [movingTo, setMovingTo] = React.useState<number | null>(null);
  /** The turn and crop being made to the picture, sent on Save. */
  const picture = useImageEdit();
  /** True while "Reset to original" is putting the upload back. */
  const [isResetting, setIsResetting] = React.useState(false);

  /*
   * The rest of the garden, for moving a photo onto the right plant.
   *
   * Read straight from the context rather than taken as a prop, and read
   * defensively: this dialog has one caller today and no reason to fall over
   * if it ever gets another one outside the provider.
   */
  const plantsContext = useContext(PlantsContext);
  /** The garden's plants, for the "On plant" list. */
  const gardenPlants = React.useMemo(
    () => plantsContext?.plants ?? [],
    [plantsContext?.plants]
  );

  const refetchPlants = plantsContext?.refetchPlants;
  /**
   * The photo has left this card, deleted or moved: tell the card and close.
   *
   * @param photoId - The photo.
   */
  const handleGone = React.useCallback((photoId: number) => {
    // The question was answered: the photo is gone, so the panel that asked
    // goes with it. It is a window of its own beside this one, and closing
    // this one used to leave it standing over the page.
    setShowDeleteConfirm(false);
    onPhotoDeleted?.(photoId);
    // A photo that left for another plant has arrived somewhere; nothing else
    // on the page knows that yet.
    refetchPlants?.();
    onClose();
  }, [onPhotoDeleted, refetchPlants, onClose]);

  /**
   * Moves the photo, and says where it went.
   *
   * Saying so is the point. A successful move closes the dialog and empties
   * the card it was opened from, and the plant it arrived at is usually
   * somewhere else on the page or off the bottom of it — so from the
   * gardener's chair a move that worked perfectly looks exactly like a
   * picture that vanished. It reads as "nothing happened", which is why this
   * was reported as not working at all.
   *
   * @param target - The plant it goes to.
   */
  const handleMove = React.useCallback(async (target: number) => {
    const destination = gardenPlants.find((p) => p.plant_obj_id === target);
    await actionsRef.current.move(target);
    toast({
      title: t('plants.photo.moved'),
      description: destination
        ? t('plants.photo.movedTo', { plant: plantName(destination) })
        : undefined,
    });
  }, [gardenPlants, toast, t]);

  const actions = usePhotoActions(subject, photo, {
    onGone: handleGone,
    onReplaced: (updated) => { onPhotoUpdated(updated); setIsChoosingPicture(false); },
  });

  /**
   * The actions, reachable from a callback defined above them.
   *
   * handleMove has to know the plant list to name the destination, and the
   * actions have to know handleMove's outcome; a ref is the smaller of the two
   * knots that solves.
   */
  const actionsRef = React.useRef(actions);
  actionsRef.current = actions;

  /**
   * Shrinks a picture before it replaces one, and takes the date it arrives
   * with, the way uploading does.
   *
   * Uploading a photo runs it through the processor first; replacing one sent
   * whatever the camera handed over, raw. A phone photograph is comfortably
   * over the server's five megabyte limit, so multer destroyed the request
   * stream and the gardener got a five hundred with an HTML page behind it.
   * Both ways in now get the same treatment.
   *
   * The date is the other half of that. The processor has always read the
   * EXIF, and this threw the answer away and kept only the file — so putting a
   * photograph from August over one from June left the field still saying
   * June, and the only clue was that the picture had changed and the date had
   * not. A new picture brings its own date; where it brings none, it is a new
   * picture and today is the honest default rather than the date of the one it
   * replaced.
   *
   * @param file - The new picture, from whichever source.
   * @param known - A date the source stated outright, which beats the file's
   *   own header. Google Photos hands over a resize with the EXIF gone.
   * @param trustExif - False for a camera capture, which was taken now
   *   whatever a re-encoded header claims.
   *
   * A picture that could not be made ready is not sent (preparePhoto, the one
   * rule for every picture): nothing is replaced and the reason is said.
   */
  const replaceWith = React.useCallback(async (
    file: File,
    { known = null, trustExif = true }: { known?: Date | null; trustExif?: boolean } = {},
  ): Promise<void> => {
    let claimed: Date | null = known;
    setPictureError(null);
    actionsRef.current.clearError();
    setIsPreparing(true);
    const prepared = await preparePhoto(file);
    setIsPreparing(false);
    if (!prepared.ok) {
      setPictureError(prepared.message);
      return;
    }
    if (!claimed && trustExif) claimed = prepared.exif.dateTaken?.toJSDate() ?? null;
    setDateTaken(claimed ?? new Date());
    await actionsRef.current.replace(prepared.file);
  }, []);

  const { stop: stopCamera } = camera;
  const { reset: resetNote } = note;
  const { clearError } = actions;
  const { clear: clearPicture } = picture;

  /*
   * Everything this visit put on screen, forgotten when it closes.
   *
   * Depends on the stable callbacks rather than the hooks that own them: each
   * hook returns a fresh object every render, so depending on the objects
   * would run this on every render.
   */
  React.useEffect(() => {
    if (isOpen) return;
    stopCamera();
    resetNote();
    clearError();
    setPictureError(null);
    setIsSaving(false);
    setIsChoosingPicture(false);
    setMovingTo(null);
    setShowDeleteConfirm(false);
    clearPicture();
    setIsResetting(false);
  }, [isOpen, stopCamera, resetNote, clearError, clearPicture]);

  // A turn or crop belongs to the picture it was made on.
  React.useEffect(() => { clearPicture(); }, [photo?.photo_id, photo?.filename, clearPicture]);

  /** The photo's id and saved day: what the date below starts from (photo itself is a new object on every refetch). */
  const photoId = photo?.photo_id;
  const savedDay = photo?.date_taken;
  React.useEffect(() => {
    if (!isOpen || photoId === undefined || savedDay === undefined) return;
    /*
     * Started again only when the screen opens, another photo is shown, or
     * the saved day itself changes, never because the garden refetched: React
     * Query hands back a new photo object on every refetch (switching apps,
     * the phone waking), and keying this on the object put the saved day back
     * over the one just picked (owner, 2026-10-14: "date picker no worky", the
     * day picked did not stay).
     *
     * The calendar day the photo claims, as a local calendar day.
     *
     * Read as the day itself (calendarDay), never through a time zone: a
     * stamp read in the local zone was once the evening before, which is how
     * pressing Update without touching anything walked a photo backwards one
     * day every time, and reading it as UTC did the same east of Greenwich.
     * Everything downstream (the calendar the gardener picks from, the field
     * that prints it) speaks in local days.
     */
    const day = calendarDay(savedDay);
    setDateTaken(DateTime.local(day.year, day.month, day.day).toJSDate());
  }, [isOpen, photoId, savedDay]);

  /**
   * Saves everything this screen says: the date, the words, and the plant.
   *
   * One button, because there was briefly a second one. Changing the plant
   * used to arm its own confirm below the dropdown, so a gardener could pick
   * the right plant, press the obvious button, and watch nothing happen — the
   * obvious button did not know about the move.
   *
   * Order matters. The words go first so a failure to save them stops the
   * whole update rather than reporting success over a note that never
   * changed; the move goes last, because everything before it addresses the
   * photo through the plant it is still on.
   */
  const handleSave = async (): Promise<void> => {
    if (!photo) return;

    setIsSaving(true);
    note.setError(null);
    try {
      // The picture first: a turn or crop that cannot be saved stops the
      // whole update, as the words do, rather than half of it going through.
      if (picture.changed) {
        try {
          const edited = await editPhotoImageFor(subject, photo.photo_id, picture.edit);
          onPhotoUpdated({ ...photo, ...edited });
          clearPicture();
        } catch (error) {
          note.setError(errorMessage(error, t('plants.photo.edit.failed')));
          return;
        }
      }

      const noteId = await note.saveForExisting();

      const updated = await updatePhotoFor(
        subject,
        photo.photo_id,
        dateTaken,
        noteId,
      );

      if (movingTo !== null) {
        // Takes the photo off this card, refetches the garden and closes, and
        // says where it went. Its words travel with it.
        await handleMove(movingTo);
        return;
      }

      // The card shows a photo's words from the plants payload, which still
      // holds the old ones. Without this the picture keeps saying whatever it
      // said before until something else refetches the garden.
      await note.sync();

      onPhotoUpdated(updated);
      onClose();
      // A photo's date can move the plant's own; bring the plant in step.
      if (isPlant) void plantsContext?.updateSinglePlant?.(String(plantObjId)).catch(() => {});
    } catch (error) {
      // Keep the dialog open with the words still in the box, so nothing
      // anybody typed is lost to a failed save. Not "the photo was saved, but
      // its note could not be", which this used to say whichever part failed:
      // the words go first and stop the whole update, so nothing was saved.
      debugLog('PhotoUploadDialog', 'Photo update error:', error);
      note.setError(errorMessage(error, movingTo !== null
        ? t('plants.photo.moveFailed')
        : t('plants.photo.saveFailed')));
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Deletes the photo, once asked, and says so in a toast when it cannot.
   *
   * The question sits in a panel over this dialog, so a line written in the
   * dialog would be hidden behind it; a failed delete used to leave the
   * panel's buttons un-greyed and nothing else. The panel stays up, so
   * Delete can be pressed again or Keep it chosen.
   */
  const handleDelete = (): void => {
    void actions.remove().catch((err: unknown) => {
      toast({
        title: errorMessage(err, t('plants.photo.deleteFailed')),
        variant: 'destructive',
      });
    });
  };

  /**
   * "Reset to original": the upload back in the edit's place, at once, and
   * any turn or crop in progress forgotten with it.
   */
  const handleReset = async (): Promise<void> => {
    if (!photo) return;
    setIsResetting(true);
    note.setError(null);
    try {
      const original = await resetPhotoImageFor(subject, photo.photo_id);
      clearPicture();
      onPhotoUpdated({ ...photo, ...original });
      if (isPlant) void plantsContext?.updateSinglePlant?.(String(plantObjId)).catch(() => {});
    } catch (error) {
      note.setError(errorMessage(error, t('plants.photo.edit.resetFailed')));
    } finally {
      setIsResetting(false);
    }
  };

  /** Any errand that would be disturbed by starting another one. */
  const busy = isSaving || isPreparing || isResetting || actions.isReplacing || actions.isRemoving || actions.isMoving;

  /*
   * Delete photo (quiet, far left), Cancel, Save. While another picture is being chosen, the foot is
   * the way back to this one instead.
   */
  const footer = isChoosingPicture ? (
    camera.isActive ? undefined : (
      <Button
        variant="outline"
        onClick={() => {
          // A Google Photos pick still waiting is stopped too: photos chosen
          // after this would otherwise replace the picture being kept.
          camera.stop(); google.cancel(); setPictureError(null); setIsChoosingPicture(false);
        }}
        disabled={isPreparing || actions.isReplacing}
      >
        {t('plants.photo.keepPicture')}
      </Button>
    )
  ) : (
    <ActionRow
      onCancel={onClose}
      disabled={busy}
      onRemove={() => setShowDeleteConfirm(true)}
      removeLabel={t('plants.photo.deleteAction')}
    >
      <Button size="lg" onClick={handleSave} loading={isSaving} disabled={busy}>
        {t('plants.photo.save')}
      </Button>
    </ActionRow>
  );

  return (
    <>
      <PhotoDialogShell
        isOpen={isOpen}
        onClose={onClose}
        title={isChoosingPicture ? t('plants.photo.replaceTitle') : t('plants.photo.editTitle')}
        footer={footer}
      >
        {isChoosingPicture ? (
          <>
            <PhotoSourcePicker
              camera={camera}
              onCaptured={(file) => {
                void replaceWith(file, { trustExif: false }).catch(() => {});
              }}
              onFileChosen={(file, takenAt) => {
                void replaceWith(file, { known: takenAt }).catch(() => {});
              }}
              onPickGoogle={google.pick}
              isPickingGoogle={google.isPicking}
              googleStatus={google.status}
              googleCount={google.count}
              onCancelGoogle={google.cancel}
              googleError={google.error}
            />
            {/* Getting the picture ready (an iPhone photo is converted first)
                and sending it are one errand to the gardener. */}
            {(isPreparing || actions.isReplacing) && (
              <div className="flex shrink-0 items-center justify-center gap-2 text-sm text-muted-foreground">
                <Spinner size={18} color="hsl(var(--primary))" />
                <span>{t('plants.photo.replacing')}</span>
              </div>
            )}
          </>
        ) : photo ? (
          <>
            {/*
              * The picture, taking what room the fields leave it. What acts on
              * the picture itself sits on it: Replace and Download top right.
              * (The check-up is the plant's, on its card, not here.) They were
              * buttons in a row of their own under a fixed 220px picture.
              */}
            <PhotoPicture
              src={null}
              alt={t('plants.photo.thisAlt')}
              busy={busy || isImageLoading}
              picture={(
                <ImageEditStage
                  src={photo.signed_photo_url || photo.cardUrl || getPhotoUrl(photo, userId, plantObjId)}
                  alt={t('plants.photo.thisAlt')}
                  state={picture}
                  onLoad={() => setIsImageLoading(false)}
                  disabled={busy}
                />
              )}
            >
              {/* These act on the picture as stored, so they step aside while
                  a turn or crop is waiting to be saved. */}
              {!picture.changed && <div className="absolute right-2 top-2 flex gap-1.5">
                <PhotoButton
                  size="pill"
                  label={t('plants.photo.replace')}
                  onClick={() => { actions.clearError(); setPictureError(null); setIsChoosingPicture(true); }}
                  disabled={busy}
                >
                  <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
                  {t('plants.photo.replace')}
                </PhotoButton>
                <PhotoButton
                  size="pill"
                  label={t('plants.photo.download')}
                  onClick={() => void actions.download()}
                  disabled={actions.isDownloading || busy}
                >
                  {actions.isDownloading
                    ? <Spinner size={14} color="currentColor" />
                    : <Download className="h-3.5 w-3.5" aria-hidden="true" />}
                </PhotoButton>
              </div>}
            </PhotoPicture>

            {/* Turn and crop: under the picture they act on. */}
            <ImageEditTools
              state={picture}
              disabled={busy}
              onReset={isEditedPhoto(photo.filename) ? () => void handleReset() : undefined}
              resetting={isResetting}
            />
            {picture.edit.crop && (
              <p className="shrink-0 text-xs text-muted-foreground">{t('plants.photo.edit.cropHint')}</p>
            )}

            {/* What this photo says about itself: which day, and the words on it. */}
            <div className="grid shrink-0 grid-cols-1 gap-2">
              <DateTakenField value={dateTaken} onChange={setDateTaken} disabled={busy} />
            </div>

            {/* Changing a photo's words is the same act as writing them, so it
                is the same field. */}
            <PhotoNoteField
              value={note.value}
              onChange={note.setValue}
              disabled={busy}
              existing
            />
            {note.error && (
              <p className="shrink-0 text-sm text-destructive"><FailureText text={note.error} /></p>
            )}

            {/* Last: which plant it is filed on, a rarer thing to be saying
                than the three above. */}
            {isPlant && <MoveToPlantField
              plants={gardenPlants}
              currentPlantObjId={plantObjId}
              chosen={movingTo}
              onChoose={(next) => { actions.clearError(); setMovingTo(next); }}
              disabled={busy}
            />}
          </>
        ) : null}

        {(actions.error ?? pictureError) && (
          <p className="shrink-0 text-sm text-destructive"><FailureText text={actions.error ?? pictureError ?? ''} /></p>
        )}
      </PhotoDialogShell>

      <DeletePhotoConfirm
        isOpen={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        onConfirm={handleDelete}
        isDeleting={actions.isRemoving}
      />
    </>
  );
};
